import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Deal, TeamMember } from "../../lib/types";
import { supabase } from "../../lib/supabase";
import { scheduleDrone } from "../../lib/droneActions";
import { useAuth } from "../../context/AuthContext";

/* global L — loaded from CDN in index.html */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const L: any;

// ---------- types ----------
type Mode = "schedule" | "reroute";

interface Props {
  deals: Deal[];
  onClose: () => void;
  onScheduled?: () => void;
  /** "schedule" (default) — full scheduling flow (date/pilot, write to dispatch_schedule).
   *  "reroute" — visualize-and-navigate only; no DB writes, no date/pilot. */
  mode?: Mode;
  /** Override the modal title for "reroute" mode. */
  title?: string;
}

interface Stop {
  deal: Deal;
  order: number;
  lat: number;
  lon: number;
  driveMin: number; // estimated drive minutes from previous stop
  flyMin: number; // 7 min per stop
}

// ---------- helpers ----------

/** Haversine distance in km between two lat/lon points. */
function haversine(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Estimate driving minutes from km (assume ~45 km/h average in suburban GA). */
function driveMinutes(km: number): number {
  return Math.round((km / 45) * 60);
}

/** Nearest-neighbor route optimization. Returns indices in visit order.
 *  If `start` is given, the first stop is the one nearest to start (so the
 *  pilot's first drive leg is from their current/chosen location). Without
 *  a start point, the queue's first item anchors the route. */
function optimizeRoute(
  points: { lat: number; lon: number }[],
  start?: { lat: number; lon: number },
): number[] {
  if (points.length <= 1) return points.map((_, i) => i);
  const visited = new Set<number>();
  const order: number[] = [];

  let current: number;
  if (start) {
    // Pick stop nearest to the start point.
    let nearest = 0;
    let nearestDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      const d = haversine(start.lat, start.lon, points[i].lat, points[i].lon);
      if (d < nearestDist) {
        nearestDist = d;
        nearest = i;
      }
    }
    current = nearest;
  } else {
    current = 0;
  }
  visited.add(current);
  order.push(current);

  while (visited.size < points.length) {
    let nearest = -1;
    let nearestDist = Infinity;
    for (let i = 0; i < points.length; i++) {
      if (visited.has(i)) continue;
      const d = haversine(
        points[current].lat,
        points[current].lon,
        points[i].lat,
        points[i].lon,
      );
      if (d < nearestDist) {
        nearestDist = d;
        nearest = i;
      }
    }
    if (nearest >= 0) {
      visited.add(nearest);
      order.push(nearest);
      current = nearest;
    }
  }
  return order;
}

/** Geocode a free-form address via Nominatim (OSM). CORS-enabled; rate-
 *  limited but a one-shot per route plan is well within courtesy limits. */
async function geocodeAddress(
  q: string,
): Promise<{ lat: number; lon: number; label: string } | null> {
  const url =
    "https://nominatim.openstreetmap.org/search?format=json&limit=1&q=" +
    encodeURIComponent(q);
  const r = await fetch(url, {
    headers: { Accept: "application/json" },
  });
  if (!r.ok) return null;
  const arr = (await r.json()) as Array<{
    lat: string;
    lon: string;
    display_name: string;
  }>;
  if (!arr || arr.length === 0) return null;
  return {
    lat: parseFloat(arr[0].lat),
    lon: parseFloat(arr[0].lon),
    label: arr[0].display_name,
  };
}

function formatAddress(d: Deal): string {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
    d.property_zip,
  ]
    .filter(Boolean)
    .join(" ");
}

function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Google Maps multi-stop directions link. If `start` is supplied it
 *  becomes the origin; otherwise the first stop is the origin. */
function googleMapsLink(
  stops: Stop[],
  start?: { lat: number; lon: number } | null,
): string {
  if (stops.length === 0) return "#";
  const stopPoints = stops.map((s) => `${s.lat},${s.lon}`);
  const points = start ? [`${start.lat},${start.lon}`, ...stopPoints] : stopPoints;
  const origin = points[0];
  const dest = points[points.length - 1];
  const waypoints = points.slice(1, -1).join("/");
  if (points.length === 1)
    return `https://www.google.com/maps/dir/?api=1&destination=${origin}&travelmode=driving`;
  if (points.length === 2)
    return `https://www.google.com/maps/dir/${origin}/${dest}`;
  return `https://www.google.com/maps/dir/${origin}/${waypoints}/${dest}`;
}

// Pin colors for numbered markers
const PIN_COLORS = [
  "#f46530", // bardin-orange
  "#345c72", // bardin-teal
  "#d4edf4", // bardin-sky
  "#2a2e30", // bardin-ink
  "#ff9e7a", // bardin-peach
];

function pinColor(idx: number): string {
  return PIN_COLORS[idx % PIN_COLORS.length];
}

// ---------- component ----------

export default function RouteBuilder({
  deals,
  onClose,
  onScheduled,
  mode = "schedule",
  title,
}: Props) {
  const isReroute = mode === "reroute";
  const { member } = useAuth();
  const mapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapInstance = useRef<any>(null);
  const [date, setDate] = useState(todayISO());
  const [pilots, setPilots] = useState<TeamMember[]>([]);
  const [pilotId, setPilotId] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Start point. Either browser geolocation or geocoded address. When set,
  // it anchors the route — first leg's drive time is start → first stop,
  // and the Google Maps link uses it as origin.
  const [startPoint, setStartPoint] = useState<{
    lat: number;
    lon: number;
    label: string;
  } | null>(null);
  const [locating, setLocating] = useState(false);
  const [addressInput, setAddressInput] = useState("");
  const [geocoding, setGeocoding] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const handleUseMyLocation = useCallback(() => {
    setStartError(null);
    if (!("geolocation" in navigator)) {
      setStartError("Geolocation not supported on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStartPoint({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          label: "Current location",
        });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        const msg =
          err.code === err.PERMISSION_DENIED
            ? "Location permission denied. Allow location for portal.bardinroofing.com in Safari settings, or enter an address."
            : err.code === err.POSITION_UNAVAILABLE
              ? "Couldn't determine your location. Try again or enter an address."
              : "Location request timed out. Try again or enter an address.";
        setStartError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  const handleGeocodeAddress = useCallback(async () => {
    const q = addressInput.trim();
    if (!q) return;
    setStartError(null);
    setGeocoding(true);
    try {
      const hit = await geocodeAddress(q);
      if (!hit) {
        setStartError("Address not found. Try a more specific address.");
        return;
      }
      setStartPoint(hit);
      setAddressInput("");
    } catch {
      setStartError("Couldn't reach the geocoder. Check your connection.");
    } finally {
      setGeocoding(false);
    }
  }, [addressInput]);

  // Filter to deals with lat/lon, build route
  const stops: Stop[] = useMemo(() => {
    const withCoords = deals.filter(
      (d) => d.property_lat != null && d.property_lon != null,
    );
    if (withCoords.length === 0) return [];

    const points = withCoords.map((d) => ({
      lat: d.property_lat!,
      lon: d.property_lon!,
    }));
    const order = optimizeRoute(
      points,
      startPoint ? { lat: startPoint.lat, lon: startPoint.lon } : undefined,
    );

    return order.map((idx, i) => {
      const d = withCoords[idx];
      const lat = d.property_lat!;
      const lon = d.property_lon!;
      let driveMin = 0;
      if (i === 0 && startPoint) {
        // First leg: from start point.
        driveMin = driveMinutes(haversine(startPoint.lat, startPoint.lon, lat, lon));
      } else if (i > 0) {
        const prev = withCoords[order[i - 1]];
        const km = haversine(
          prev.property_lat!,
          prev.property_lon!,
          lat,
          lon,
        );
        driveMin = driveMinutes(km);
      }
      return { deal: d, order: i + 1, lat, lon, driveMin, flyMin: 7 };
    });
  }, [deals, startPoint]);

  const noCoords = deals.filter(
    (d) => d.property_lat == null || d.property_lon == null,
  );

  const totalFlyMin = stops.length * 7;
  const totalDriveMin = stops.reduce((s, st) => s + st.driveMin, 0);
  const totalMin = totalFlyMin + totalDriveMin;
  const hrs = Math.floor(totalMin / 60);
  const mins = totalMin % 60;

  // Load pilots (skip in reroute mode — already-scheduled deals don't need them)
  useEffect(() => {
    if (isReroute) return;
    supabase
      .from("team_roster")
      .select("*")
      .or("role.eq.drone_pilot,secondary_role.eq.drone_pilot")
      .eq("is_active", true)
      .order("full_name")
      .then(({ data }) => {
        const list = (data ?? []) as TeamMember[];
        setPilots(list);
        const self = list.find((m) => m.id === member?.id);
        setPilotId(self?.id ?? list[0]?.id ?? "");
      });
  }, [isReroute]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build Leaflet map
  useEffect(() => {
    if (!mapRef.current || typeof L === "undefined" || stops.length === 0)
      return;

    // Clean up previous map if any
    if (mapInstance.current) {
      mapInstance.current.remove();
      mapInstance.current = null;
    }

    const map = L.map(mapRef.current, { zoomControl: true });
    mapInstance.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 18,
    }).addTo(map);

    const bounds = L.latLngBounds([]);

    // Start marker (green "S") if user picked a start point.
    if (startPoint) {
      const startIcon = L.divIcon({
        className: "",
        html: `<div style="
          width:32px; height:32px; border-radius:50%;
          background:#16a34a; color:#fff; font-weight:700;
          display:flex; align-items:center; justify-content:center;
          font-size:14px; border:2px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,.3);
        ">S</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
      L.marker([startPoint.lat, startPoint.lon], { icon: startIcon })
        .addTo(map)
        .bindPopup(`<b>Start</b><br/>${startPoint.label}`);
      bounds.extend([startPoint.lat, startPoint.lon]);
    }

    // Add numbered markers
    stops.forEach((s) => {
      const color = pinColor(s.order - 1);
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:32px; height:32px; border-radius:50%;
          background:${color}; color:#fff; font-weight:700;
          display:flex; align-items:center; justify-content:center;
          font-size:14px; border:2px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,.3);
        ">${s.order}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
      L.marker([s.lat, s.lon], { icon })
        .addTo(map)
        .bindPopup(
          `<b>#${s.order} ${s.deal.homeowner_name ?? ""}</b><br/>${formatAddress(s.deal)}`,
        );
      bounds.extend([s.lat, s.lon]);
    });

    // Draw route polyline (start → stops if start set, else stops only).
    const latlngs: [number, number][] = startPoint
      ? [[startPoint.lat, startPoint.lon], ...stops.map((s) => [s.lat, s.lon] as [number, number])]
      : stops.map((s) => [s.lat, s.lon] as [number, number]);
    L.polyline(latlngs, {
      color: "#f46530",
      weight: 3,
      opacity: 0.7,
      dashArray: "8 6",
    }).addTo(map);

    map.fitBounds(bounds.pad(0.15));

    return () => {
      map.remove();
      mapInstance.current = null;
    };
  }, [stops, startPoint]);

  // Schedule all
  const scheduleAll = useCallback(async () => {
    if (!date || !pilotId) {
      setError("Pick a date and pilot.");
      return;
    }
    setScheduling(true);
    setError(null);
    try {
      for (const s of stops) {
        await scheduleDrone(
          s.deal,
          { date, pilotId },
          member?.id ?? null,
        );
      }
      setDone(true);
      setTimeout(() => {
        onScheduled?.();
        onClose();
      }, 1200);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setScheduling(false);
    }
  }, [date, pilotId, stops, member, onScheduled, onClose]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-bardin-ink text-white">
        <div>
          <h2 className="text-lg font-semibold">
            {title ?? (isReroute ? "Re-route" : "Route Builder")} — {stops.length} stop{stops.length !== 1 ? "s" : ""}
          </h2>
          <p className="text-sm text-slate-300">
            {hrs > 0 ? `${hrs}h ` : ""}
            {mins}m estimated · {totalFlyMin}m flying + {totalDriveMin}m driving
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-white/70 hover:text-white text-2xl leading-none px-2"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Body: map + list side-by-side on desktop, stacked on mobile */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Map */}
        <div className="h-64 md:h-auto md:flex-1 relative">
          <div ref={mapRef} className="absolute inset-0" />
          {stops.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-500">
              No stops with coordinates
            </div>
          )}
        </div>

        {/* Stop list + controls */}
        <div className="md:w-96 flex flex-col border-l border-slate-200 bg-white overflow-hidden">
          {/* Scrollable stop list */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {stops.map((s) => (
              <div
                key={s.deal.id}
                className="flex gap-3 items-start bg-slate-50 rounded-lg p-3"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                  style={{ background: pinColor(s.order - 1) }}
                >
                  {s.order}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm text-bardin-ink truncate">
                    {s.deal.homeowner_name ?? "(no name)"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {formatAddress(s.deal)}
                  </div>
                  <div className="flex gap-3 mt-1 text-xs text-slate-400">
                    <span>7 min fly</span>
                    {s.driveMin > 0 && (
                      <span>+ {s.driveMin} min drive</span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {noCoords.length > 0 && (
              <div className="bg-amber-50 text-amber-800 text-xs rounded-lg p-3">
                <span className="font-medium">
                  {noCoords.length} deal{noCoords.length > 1 ? "s" : ""} skipped
                </span>
                {" "}— missing coordinates:{" "}
                {noCoords
                  .map((d) => d.homeowner_name ?? "(no name)")
                  .join(", ")}
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="border-t border-slate-200 p-3 space-y-3 bg-slate-50">
            {/* Start point picker */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Start from
              </label>
              {startPoint ? (
                <div className="flex items-center gap-2 bg-white border border-slate-300 rounded px-2 py-1.5">
                  <span className="text-green-600 text-sm">📍</span>
                  <span className="flex-1 text-sm text-slate-700 truncate" title={startPoint.label}>
                    {startPoint.label}
                  </span>
                  <button
                    onClick={() => {
                      setStartPoint(null);
                      setStartError(null);
                    }}
                    className="text-xs text-slate-400 hover:text-slate-700"
                    title="Clear start point"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <button
                    onClick={handleUseMyLocation}
                    disabled={locating}
                    className="w-full px-3 py-1.5 rounded bg-white border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    📍 {locating ? "Getting location…" : "Use my current location"}
                  </button>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={addressInput}
                      onChange={(e) => setAddressInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleGeocodeAddress();
                        }
                      }}
                      placeholder="Or enter address"
                      className="flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                    />
                    <button
                      onClick={handleGeocodeAddress}
                      disabled={geocoding || !addressInput.trim()}
                      className="px-3 py-1.5 rounded bg-bardin-teal text-white text-sm hover:opacity-90 disabled:opacity-50"
                    >
                      {geocoding ? "…" : "Set"}
                    </button>
                  </div>
                </div>
              )}
              {startError && (
                <div className="bg-amber-50 text-amber-800 text-xs rounded p-2 mt-1.5">
                  {startError}
                </div>
              )}
            </div>

            {!isReroute && (
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Pilot
                  </label>
                  <select
                    value={pilotId}
                    onChange={(e) => setPilotId(e.target.value)}
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
                  >
                    {pilots.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-700 text-xs rounded p-2">
                {error}
              </div>
            )}

            {done && (
              <div className="bg-green-50 text-green-700 text-sm rounded p-2 text-center font-medium">
                All {stops.length} stops scheduled!
              </div>
            )}

            <div className="flex gap-2">
              <a
                href={googleMapsLink(stops, startPoint)}
                target="_blank"
                rel="noreferrer"
                className={`${isReroute ? "flex-1" : "flex-1"} px-3 py-2 rounded bg-bardin-teal text-white text-sm text-center hover:opacity-90`}
              >
                Open in Google Maps
              </a>
              {!isReroute && (
                <button
                  onClick={scheduleAll}
                  disabled={scheduling || done || stops.length === 0}
                  className="flex-1 px-3 py-2 rounded bg-bardin-orange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {scheduling
                    ? "Scheduling…"
                    : done
                      ? "Done!"
                      : `Schedule all ${stops.length}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
